import type { FlowKind, FlowStep } from "../core/contracts";
import { ORE_FLOW } from "./ore";

const runtime = (note: string) =>
  ({
    kind: "runtime",
    label: "Runtime verified",
    note,
  }) as const;

const ghidra = (note: string) =>
  ({
    kind: "ghidra",
    label: "Ghidra mapped",
    note,
  }) as const;

const modeled = (note: string) =>
  ({
    kind: "modeled",
    label: "Scaled model",
    note,
  }) as const;

export const SELECT_FLOW: readonly FlowStep[] = [
  {
    id: "client-request",
    label: "Client launches the SELECT",
    shortLabel: "SQL*Net",
    district: "client",
    event: "oracle.net.request",
    summary:
      "A SQL packet enters the city on the blue Oracle Net expressway. One visible packet represents the framed request and its binds.",
    evidence: modeled(
      "Oracle Net framing is compressed into one visible packet.",
    ),
    functionChain: ["client", "Oracle Net", "TTC"],
    phase: "network",
    action: "packet",
    targets: ["listener", "server"],
    durationMs: 620,
  },
  {
    id: "server-process",
    label: "Dedicated server accepts the call",
    shortLabel: "Dispatch",
    district: "server",
    event: "server.call.accepted",
    summary:
      "The dedicated server process unwraps the call and enters the Oracle program interface before parse.",
    evidence: runtime(
      "The controlled server-process stack crossed ttcpip, opiodr, kpoal8, kpooprx and opiosq0.",
    ),
    functionChain: ["ttcpip", "opiodr", "kpoal8", "kpooprx", "opiosq0"],
    phase: "network",
    action: "dispatch",
    durationMs: 680,
  },
  {
    id: "parse-entry",
    label: "Parser opens the statement",
    shortLabel: "Parser",
    district: "parser",
    event: "cursor.parse.entry",
    summary:
      "The parser tokenizes the statement, resolves names and begins cursor construction for a uniquely tagged hard parse.",
    evidence: runtime(
      "A tagged SELECT reached kksParseCursor on Oracle 19.25 AArch64.",
    ),
    functionChain: ["opiosq0", "kksParseCursor", "kkspsc0", "kksfbc"],
    phase: "parse",
    action: "parse",
    durationMs: 760,
  },
  {
    id: "sql-hash",
    label: "SQL text hashes into the library cache",
    shortLabel: "SQL hash",
    district: "library-cache",
    event: "library.cache.hash",
    summary:
      "The SQL identity selects a library-cache bucket. OraCity lights the parent-cursor tower and its hash avenue.",
    evidence: ghidra(
      "The kksfbc path and KGL lookup machinery are mapped statically; the displayed bucket number is illustrative.",
    ),
    functionChain: ["kksfbc", "kglget", "library cache bucket"],
    phase: "library-cache",
    action: "hash",
    facts: {
      "SQL ID": "7xw0… (visualized)",
      Bucket: "0x04A7 (scaled)",
    },
    durationMs: 700,
  },
  {
    id: "library-bucket-lock",
    label: "Library-cache bucket is protected",
    shortLabel: "Bucket lock",
    district: "mutex-gate",
    event: "library.cache.bucket.protect",
    summary:
      "The server crosses the first concurrency gate before it can walk handles in the selected bucket.",
    evidence: ghidra(
      "Bucket protection is mapped from the KGL path. Exact lock mode and address remain build- and state-specific.",
    ),
    functionChain: ["kglget", "kglLock", "KGLHDBMTX"],
    phase: "library-cache",
    action: "mutex-acquire",
    durationMs: 620,
  },
  {
    id: "parent-cursor",
    label: "Parent cursor handle found",
    shortLabel: "Parent",
    district: "library-cache",
    event: "library.cache.parent.found",
    summary:
      "The parent cursor is the shared identity for the SQL text. Compatible executable children hang beneath it.",
    evidence: runtime(
      "The controlled parse reached kkscsSearchChildList from kksfbc after locating the parent.",
    ),
    functionChain: ["kksfbc", "kkscsSearchChildList"],
    phase: "library-cache",
    action: "hash",
    durationMs: 660,
  },
  {
    id: "cursor-mutex",
    label: "Cursor mutex X is acquired",
    shortLabel: "Mutex X",
    district: "mutex-gate",
    event: "cursor.mutex.exclusive",
    summary:
      "The mutex castle closes behind the server process while it protects cursor state for child-list inspection and loading.",
    evidence: ghidra(
      "The mapped parse path is kglhdgn → kglGetMutex → kgxExclusive; contention diverts into kgxWait.",
    ),
    functionChain: ["kglhdgn", "kglGetMutex", "kgxExclusive", "kgxWait"],
    phase: "library-cache",
    action: "mutex-acquire",
    facts: {
      Mode: "exclusive",
      Contended: "no",
    },
    durationMs: 820,
  },
  {
    id: "child-zero-scan",
    label: "Child cursor 0 is inspected",
    shortLabel: "Child 0",
    district: "library-cache",
    event: "cursor.child.scan",
    summary:
      "Oracle walks the existing child list and evaluates whether child 0 can be shared by this session.",
    evidence: runtime(
      "The traced path reached kkscsSearchChildList → kkscsCheckCursor → kkscsCheckCriteria.",
    ),
    functionChain: [
      "kkscsSearchChildList",
      "kkscsCheckCursor",
      "kkscsCheckCriteria",
    ],
    phase: "library-cache",
    action: "child-scan",
    facts: {
      Child: "0",
      "Plan hash": "2947087609",
    },
    durationMs: 760,
  },
  {
    id: "child-zero-reject",
    label: "Child 0 fails optimizer compatibility",
    shortLabel: "Mismatch",
    district: "library-cache",
    event: "cursor.child.reject",
    summary:
      "The first executable child was compiled under a different optimizer environment, so it cannot be shared.",
    evidence: runtime(
      "Two controlled executions produced child 1 with OPTIMIZER_MISMATCH=Y and a distinct optimizer environment hash.",
    ),
    functionChain: ["kkscsCheckCriteria", "OPTIMIZER_MISMATCH"],
    phase: "library-cache",
    action: "child-reject",
    facts: {
      "Child 0 env": "3070863379",
      "Requested env": "1902962930",
    },
    durationMs: 720,
  },
  {
    id: "child-one-create",
    label: "A new child cursor is born",
    shortLabel: "Child 1",
    district: "library-cache",
    event: "cursor.child.create",
    summary:
      "No compatible child exists. Oracle allocates a child for compilation under this optimizer environment; transformation and plan construction follow.",
    evidence: runtime(
      "The lab produced child 0 and child 1 for identical SQL; child 1 reports OPTIMIZER_MISMATCH=Y.",
    ),
    functionChain: ["kksLoadChild", "kksAllocHeap0", "kksAllocCursorStat"],
    phase: "library-cache",
    action: "child-create",
    facts: {
      "New child": "1",
      State: "Allocated / compilation follows",
    },
    durationMs: 940,
  },
  {
    id: "cursor-mutex-release",
    label: "Cursor mutex opens",
    shortLabel: "Unlock",
    district: "mutex-gate",
    event: "cursor.mutex.release",
    summary:
      "The protected cursor update is complete and the mutex gate reopens for waiting sessions.",
    evidence: modeled(
      "Release timing is compressed so the concurrency boundary remains readable.",
    ),
    functionChain: ["cursor state complete", "mutex release"],
    phase: "library-cache",
    action: "mutex-release",
    durationMs: 460,
  },
  {
    id: "query-transform",
    label: "Query transformer rewrites the query block",
    shortLabel: "Transform",
    district: "transformer",
    event: "query.transform",
    summary:
      "Transformation drivers traverse query blocks and consider predicate movement, join transformations, grouping and view rewrites.",
    evidence: runtime(
      "The tagged hard parse reached kkqdrv and transformation drivers including kkqctdrvGBP, kkqctdrvTD and kkqctdrvIT.",
    ),
    functionChain: ["kkqdrv", "kkqctdrvGBP", "kkqctdrvTD", "kkqctdrvIT"],
    phase: "transform",
    action: "transform",
    durationMs: 1040,
  },
  {
    id: "optimizer-cost",
    label: "Optimizer costs the transformed query",
    shortLabel: "CBO",
    district: "optimizer",
    event: "optimizer.cost",
    summary:
      "The cost-based optimizer compares access paths and join orders for the transformed query block.",
    evidence: runtime(
      "The hard-parse trace hit kkoqbc beneath kkqctFullCostTransfQB and kkqdrv.",
    ),
    functionChain: ["kkqctFullCostTransfQB", "kkoqbc", "cost model"],
    phase: "optimize",
    action: "transform",
    durationMs: 1000,
  },
  {
    id: "plan-root",
    label: "Execution plan: SELECT and ordering",
    shortLabel: "Plan root",
    district: "plan",
    event: "plan.materialize.root",
    summary:
      "The chosen plan begins with SELECT STATEMENT, SORT ORDER BY and HASH GROUP BY.",
    evidence: runtime(
      "V$SQL_PLAN for the controlled statement reports plan hash 3896265596 and cost 163.",
    ),
    functionChain: ["SELECT STATEMENT", "SORT ORDER BY", "HASH GROUP BY"],
    phase: "optimize",
    action: "plan-node",
    facts: {
      Cost: "163",
      Cardinality: "62",
    },
    durationMs: 820,
  },
  {
    id: "plan-join",
    label: "Execution plan: HASH JOIN",
    shortLabel: "Hash join",
    district: "join",
    event: "plan.materialize.join",
    summary:
      "The two branches meet at a HASH JOIN on customer ID before grouping and ordering.",
    evidence: runtime(
      "V$SQL_PLAN reports HASH JOIN with access predicate C.ID=O.CUSTOMER_ID.",
    ),
    functionChain: ["HASH JOIN", "C.ID = O.CUSTOMER_ID"],
    phase: "optimize",
    action: "plan-node",
    durationMs: 740,
  },
  {
    id: "plan-index",
    label: "Plan branch: INDEX RANGE SCAN",
    shortLabel: "Index plan",
    district: "index",
    event: "plan.materialize.index",
    summary:
      "The selective customer branch uses ORACITY_SELECT_CUSTOMERS_IX for IDs 200 through 260, then visits table rows by ROWID.",
    evidence: runtime(
      "V$SQL_PLAN reports INDEX RANGE SCAN followed by TABLE ACCESS BY INDEX ROWID BATCHED.",
    ),
    functionChain: ["INDEX RANGE SCAN", "TABLE ACCESS BY INDEX ROWID BATCHED"],
    phase: "optimize",
    action: "plan-node",
    facts: {
      Cardinality: "62",
      Cost: "4",
    },
    durationMs: 820,
  },
  {
    id: "plan-full",
    label: "Plan branch: TABLE ACCESS FULL",
    shortLabel: "Full plan",
    district: "table",
    event: "plan.materialize.full",
    summary:
      "The orders branch scans the table and filters OPEN rows whose customer IDs fall in the same range.",
    evidence: runtime(
      "V$SQL_PLAN reports TABLE ACCESS FULL on ORACITY_SELECT_ORDERS with cost 157.",
    ),
    functionChain: ["TABLE ACCESS FULL", "filter O.STATUS='OPEN'"],
    phase: "optimize",
    action: "plan-node",
    facts: {
      Cardinality: "621",
      Cost: "157",
    },
    durationMs: 820,
  },
  {
    id: "execute-open",
    label: "Row-source tree opens",
    shortLabel: "Open",
    district: "server",
    event: "rowsource.open",
    summary:
      "The server process opens the row-source tree and starts pulling rows from the two scan branches.",
    evidence: runtime(
      "Fetch tracing reached opifch2 and the qer row-source execution family.",
    ),
    functionChain: ["opifch2", "qer fetch loop"],
    phase: "execute",
    action: "row-source",
    durationMs: 620,
  },
  {
    id: "index-range-scan",
    label: "Index range scan finds ROWIDs",
    shortLabel: "Index scan",
    district: "index",
    event: "rowsource.index.range",
    summary:
      "The blue index tower walks the bounded key range and emits ROWIDs for customer table blocks.",
    evidence: ghidra(
      "qerixFetch and qerixFetchAndEqualRangeScan are present in the matching binary; plan shape is runtime verified.",
    ),
    functionChain: ["qerixFetch", "qerixFetchAndEqualRangeScan", "ROWID"],
    phase: "execute",
    action: "row-source",
    durationMs: 1000,
  },
  {
    id: "sequential-read",
    label: "Single-block I/O enters the sequential queue",
    shortLabel: "Sequential",
    district: "io-queue",
    event: "io.db_file_sequential_read",
    summary:
      "An index or ROWID-driven miss requests one block. The request waits in the yellow single-block lane before storage completes it.",
    evidence: runtime(
      "The lab defines db file sequential read as file#, block#, blocks and records active system waits for it.",
    ),
    functionChain: ["db file sequential read", "ksfdread", "datafile 1"],
    phase: "io",
    action: "io-queue",
    facts: {
      "file#": "1",
      "block#": "114385",
      blocks: "1",
    },
    durationMs: 1120,
  },
  {
    id: "single-block-flight",
    label: "The index block flies into memory",
    shortLabel: "1 block",
    district: "buffer-cache",
    event: "io.block.single.complete",
    summary:
      "A single 8 KB block arrives from the datafile and is prepared for insertion into the buffer cache.",
    evidence: runtime(
      "X$BH contained the lab index block at file 1 block 114385 with touch count 9.",
    ),
    functionChain: ["datafile", "8 KB block", "buffer cache"],
    phase: "buffer-cache",
    action: "block-flight",
    facts: {
      "X$BH HLADDR": "000000012AD86248",
      TCH: "9",
    },
    durationMs: 920,
  },
  {
    id: "buffer-hash",
    label: "Buffer identity hashes to a chain",
    shortLabel: "BH hash",
    district: "hash-chains",
    event: "buffer.hash.compute",
    summary:
      "Oracle combines the file and block identity to select a cache buffers chain. The city routes the arriving block to one lit bucket.",
    evidence: ghidra(
      "kcb_hash_num and its veneer are mapped in the matching Oracle binary.",
    ),
    functionChain: ["file#", "block#", "kcb_hash_num", "hash bucket"],
    phase: "buffer-cache",
    action: "hash-chain",
    durationMs: 760,
  },
  {
    id: "cbc-latch",
    label: "Cache buffers chains latch is acquired",
    shortLabel: "CBC latch",
    district: "hash-chains",
    event: "buffer.cbc_latch.acquire",
    summary:
      "The red latch castle serializes the short hash-chain operation before the buffer header can be linked or found.",
    evidence: ghidra(
      "kcb_trim_hash_chain calls kslgetl in static analysis; V$LATCH recorded cache buffers chains gets, misses and sleeps.",
    ),
    functionChain: ["kcb_trim_hash_chain", "kslgetl", "cache buffers chains"],
    phase: "buffer-cache",
    action: "latch-acquire",
    facts: {
      Gets: "983,605",
      Misses: "35",
      Sleeps: "6",
    },
    durationMs: 880,
  },
  {
    id: "buffer-link",
    label: "Buffer header joins the hash chain",
    shortLabel: "Chain link",
    district: "buffer-cache",
    event: "buffer.chain.link",
    summary:
      "The new buffer header is linked under the selected bucket and becomes visible to later consistent gets.",
    evidence: ghidra(
      "The buffer-get family kcbzgb and chain trimming machinery are mapped; the visible chain length is scaled.",
    ),
    functionChain: ["kcbzgb", "buffer header", "hash chain"],
    phase: "buffer-cache",
    action: "hash-chain",
    durationMs: 780,
  },
  {
    id: "table-full-scan",
    label: "Orders table begins a full scan",
    shortLabel: "Full scan",
    district: "table",
    event: "rowsource.table.full",
    summary:
      "The black table district advances through orders blocks and asks the I/O layer for a run of adjacent blocks.",
    evidence: runtime(
      "The tagged query hit qertbFetch in the row-source fetch stack and V$SQL_PLAN reports TABLE ACCESS FULL.",
    ),
    functionChain: ["qertbFetch", "TABLE ACCESS FULL", "ORACITY_SELECT_ORDERS"],
    phase: "execute",
    action: "row-source",
    durationMs: 960,
  },
  {
    id: "scattered-read",
    label: "Multi-block I/O fills the scattered queue",
    shortLabel: "Scattered",
    district: "io-queue",
    event: "io.db_file_scattered_read",
    summary:
      "A full-scan request groups adjacent blocks. Four red blocks leave the queue together and may land in non-contiguous cache buffers.",
    evidence: runtime(
      "The lab defines db file scattered read as file#, block#, blocks and records active system waits for it.",
    ),
    functionChain: ["db file scattered read", "ksfdread", "multi-block read"],
    phase: "io",
    action: "io-queue",
    facts: {
      "file#": "1",
      "first block#": "114393",
      blocks: "4 (visualized)",
    },
    durationMs: 1260,
  },
  {
    id: "multi-block-flight",
    label: "A convoy of table blocks reaches cache",
    shortLabel: "4 blocks",
    district: "buffer-cache",
    event: "io.block.multi.complete",
    summary:
      "The red convoy lands across multiple cache slots. Each block independently hashes to its own chain and briefly crosses a latch gate.",
    evidence: runtime(
      "X$BH exposed consecutive lab table blocks 114393–114397 with their distinct HLADDR values and touch counts.",
    ),
    functionChain: [
      "blocks 114393…114397",
      "kcb_hash_num",
      "kslgetl",
      "buffer cache",
    ],
    phase: "buffer-cache",
    action: "block-flight",
    durationMs: 1320,
  },
  {
    id: "hash-join",
    label: "Rows meet in the hash join",
    shortLabel: "Join",
    district: "join",
    event: "rowsource.hash_join",
    summary:
      "Customer rows build the small hash input; filtered order rows probe it before grouping and ordering.",
    evidence: runtime(
      "The controlled runtime plan reports HASH JOIN, HASH GROUP BY and SORT ORDER BY in this order.",
    ),
    functionChain: ["HASH JOIN", "HASH GROUP BY", "SORT ORDER BY"],
    phase: "execute",
    action: "row-source",
    durationMs: 1100,
  },
  {
    id: "return-rows",
    label: "Result rows race back to the client",
    shortLabel: "Rows out",
    district: "client",
    event: "oracle.net.response",
    summary:
      "Twelve grouped result rows leave the server on the blue return carriageway and complete the SELECT journey.",
    evidence: runtime(
      "The controlled query returned 12 customer groups; packet count and speed are scaled.",
    ),
    functionChain: ["opifch2", "Oracle Net", "client"],
    phase: "return",
    action: "rows-return",
    facts: {
      "Rows returned": "12",
      "Plan hash": "3896265596",
    },
    durationMs: 980,
  },
];

export const UPDATE_FLOW: readonly FlowStep[] = [
  {
    id: "update-request",
    label: "UPDATE request",
    shortLabel: "Client",
    district: "client",
    event: "oracle.net.request",
    summary:
      "A client submits an UPDATE. Bind processing and protocol framing are condensed into one request.",
    evidence: modeled("The visual event groups transport and bind setup."),
    functionChain: ["client", "Oracle Net"],
    phase: "network",
    action: "packet",
    durationMs: 650,
  },
  {
    id: "update-execute",
    label: "UPDATE row-source execution",
    shortLabel: "Update",
    district: "execution",
    event: "rowsource.update",
    summary:
      "The UPDATE row source locates the target row and enters the kernel data layer.",
    evidence: runtime(
      "The controlled UPDATE reached qerupFetch, qerupUpdRow, updrow and kauupd.",
    ),
    functionChain: ["updexe", "qerupFetch", "qerupUpdRow", "updrow", "kauupd"],
    phase: "execute",
    action: "row-source",
    durationMs: 1000,
  },
  {
    id: "current-get",
    label: "Current-mode buffer get",
    shortLabel: "CU get",
    district: "buffer-cache",
    event: "buffer.current_get",
    summary:
      "Oracle pins the current block for change rather than constructing a read-consistent image.",
    evidence: runtime(
      "The test UPDATE hit kcbgcur through kdddgb, ktbgcur and the update stack.",
    ),
    functionChain: ["kdusru", "kdddgb", "ktbgcur", "kcbgcur"],
    phase: "buffer-cache",
    action: "row-source",
    durationMs: 1050,
  },
  {
    id: "undo",
    label: "Undo record",
    shortLabel: "Undo",
    district: "undo",
    event: "transaction.undo",
    summary:
      "The transaction records the information needed for rollback and consistent-read reconstruction.",
    evidence: ghidra(
      "Undo participation is mapped from the transaction path; byte-level animation is deliberately abstract.",
    ),
    functionChain: ["transaction layer", "undo record", "undo block"],
    phase: "execute",
    action: "row-source",
    durationMs: 950,
  },
  {
    id: "dirty-buffer",
    label: "Dirty buffer and redo change vectors",
    shortLabel: "Change",
    district: "buffer-cache",
    event: "buffer.changed",
    summary:
      "The in-memory block becomes dirty while change vectors are assembled for redo.",
    evidence: runtime(
      "Commit tracing observed kcbchg1 feeding kcrfw_redo_gen_ext.",
    ),
    functionChain: ["kcbchg1", "kcrfw_redo_gen_ext"],
    phase: "execute",
    action: "row-source",
    durationMs: 1080,
  },
  {
    id: "redo-buffer",
    label: "Redo enters the log buffer",
    shortLabel: "Redo",
    district: "redo",
    event: "redo.generated",
    summary:
      "Redo change vectors enter the SGA redo path. One amber packet represents a scaled group of bytes.",
    evidence: runtime(
      "The commit stack reached kcrfw_redo_gen_ext and kcrf_commit_force_int.",
    ),
    functionChain: ["kcrfw_redo_gen_ext", "kcrf_commit_force_int"],
    phase: "execute",
    action: "packet",
    durationMs: 1050,
  },
  {
    id: "commit",
    label: "Transaction commit",
    shortLabel: "Commit",
    district: "commit",
    event: "transaction.commit",
    summary:
      "Oracle commits the transaction and forces commit redo. The first slice models the LGWR acknowledgement boundary.",
    evidence: runtime(
      "Observed xctCommitTxn → ktcCommitTxn → ktucmt → kcbchg1 → redo generation.",
    ),
    functionChain: [
      "xctCommitTxn",
      "ktdcmt",
      "ktcCommitTxn",
      "ktcCommitTxn_new",
      "ktucmt",
    ],
    phase: "execute",
    action: "row-source",
    durationMs: 1280,
  },
  {
    id: "lgwr",
    label: "LGWR durability boundary",
    shortLabel: "LGWR",
    district: "lgwr",
    event: "redo.flush",
    summary:
      "LGWR writes the protected redo range. Exact posting and wait mechanics are a future runtime trace.",
    evidence: modeled(
      "The durability boundary is accurate; timing and process-post details are not yet traced in this build.",
    ),
    functionChain: ["redo buffer", "LGWR", "online redo log"],
    phase: "io",
    action: "io-queue",
    durationMs: 1350,
  },
  {
    id: "commit-ack",
    label: "Commit acknowledged",
    shortLabel: "ACK",
    district: "client",
    event: "commit.acknowledged",
    summary:
      "The server acknowledges commit after the modeled durability boundary.",
    evidence: modeled("Network and scheduling latency are scaled."),
    functionChain: ["LGWR acknowledgement", "server process", "client"],
    phase: "return",
    action: "rows-return",
    durationMs: 720,
  },
];

export const FLOWS: Record<FlowKind, readonly FlowStep[]> = {
  ore: ORE_FLOW,
  select: SELECT_FLOW,
  update: UPDATE_FLOW,
};
